import { useState, useEffect, useRef, useMemo, Fragment } from "react";
import { Dialog, DialogPanel, Transition, TransitionChild, DialogTitle } from "@headlessui/react";
import {
    PlusIcon,
    XMarkIcon,
    PaperClipIcon,
    EyeIcon,
    DocumentIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { toast } from "sonner";

// Local Imports
import { formatBytes } from "utils/formatBytes";
import { JWT_HOST_API } from "configs/auth.config";
import { getAttachmentPreviewUrl } from "services/documentUploadService";

// Existing attachment refs point at S3 keys (communication-attachments/{companyId}/...) and need a
// presigned URL from the backend; only genuinely legacy local paths or already-absolute/blob/data
// URLs can be resolved directly.
const isStorageKey = (url) => {
    if (!url || typeof url !== "string") return false;
    if (url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:")) return false;
    return url.replace(/\\/g, "/").toLowerCase().indexOf("uploads/") === -1;
};

const getPlainTextLength = (htmlStr) => {
    if (!htmlStr) return 0;
    if (typeof window !== "undefined") {
        const doc = new DOMParser().parseFromString(htmlStr, "text/html");
        return (doc.body.textContent || "").length;
    }
    return htmlStr.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").length;
};
import {
    Input,
    Button
} from "components/ui";
import { Listbox as StyledListbox } from "components/shared/form/Listbox";
import { RichTextEditor } from "components/shared/RichTextEditor";
import { EmailScheduleToggle } from "./EmailScheduleToggle";
import { ManageVariablesModal } from "components/shared/ManageVariablesModal";

// Services
import { extractTemplateVariables, replaceTemplateVariables } from "services/templateService";

const stripHtml = (htmlString) => {
    if (!htmlString) return "";
    return htmlString
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .trim();
};

/**
 * ActivityComposer Component
 * 
 * A reusable component for composing Emails and SMS with template support.
 * Features:
 * - Switch between "Use Template" and "Write Custom" modes.
 * - Template selection and variable replacement.
 * - Email features: Subject, CC, BCC, Attachments.
 * - SMS features: Rich text editor with variable support and character count.
 */
export const ActivityComposer = ({
    followUp,
    type = "1",
    lead,
    emailSubject,
    setEmailSubject,
    emailBody,
    setEmailBody,
    smsBody,
    setSmsBody,
    ccList,
    setCcList,
    bccList,
    setBccList,
    attachments,
    setAttachments,
    preferredDeliveryDate,
    setPreferredDeliveryDate,
    preferredDeliveryTime,
    setPreferredDeliveryTime,
    emailTemplate,
    setEmailTemplate,
    isScheduleDisabled = false,
    isScheduleDeliveryEnabled = false,
    setIsScheduleDeliveryEnabled = () => { },
    errors = {},
    setErrors = () => { },
    emailTemplates,
    smsTemplates,
    templatesLoading = false,
}) => {
    const isEmail = Number(type) === 1;
    const isSMS = Number(type) === 2;

    // Internal action states
    const [emailAction, setEmailAction] = useState(followUp?.EmailTemplateId ? "template" : "compose");
    const [smsAction, setSmsAction] = useState(followUp?.SmsTemplateId ? "template" : "compose");

    const [templateVariables, setTemplateVariables] = useState({});
    const passedTemplates = isEmail ? emailTemplates : isSMS ? smsTemplates : null;
    const templates = useMemo(() => {
        const list = passedTemplates || [];
        return [{ id: 1, label: "-- Select Template --" }, ...list];
    }, [passedTemplates]);

    // toggleScheduleEmail is controlled by the parent via isScheduleDeliveryEnabled / setIsScheduleDeliveryEnabled


    // CC / BCC input states
    const [showCc, setShowCc] = useState(ccList.length > 0);
    const [showBcc, setShowBcc] = useState(bccList.length > 0);
    const [ccInput, setCcInput] = useState("");
    const [bccInput, setBccInput] = useState("");
    const [isDragging, setIsDragging] = useState(false);

    // RTE modal states (placeholders)
    const [showImageModal, setShowImageModal] = useState(false);
    const [showHtmlModal, setShowHtmlModal] = useState(false);
    const [imageUrl, setImageUrl] = useState("");
    const [imageWidth, setImageWidth] = useState("300");
    const [imageHeight, setImageHeight] = useState("200");
    const [htmlContent, setHtmlContent] = useState("");
    const [htmlContentToInsert, setHtmlContentToInsert] = useState("");
    const [smsHtmlContentToInsert, setSmsHtmlContentToInsert] = useState("");
    const [showVariablesModal, setShowVariablesModal] = useState(false);
    const [activeEditor, setActiveEditor] = useState(null);
    const [editingImage, setEditingImage] = useState(null);

    // Clear smsHtmlContentToInsert after a short delay to allow the RichTextEditor to process it
    useEffect(() => {
        if (smsHtmlContentToInsert) {
            const timer = setTimeout(() => {
                setSmsHtmlContentToInsert("");
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [smsHtmlContentToInsert]);

    // Clear htmlContentToInsert after a short delay
    useEffect(() => {
        if (htmlContentToInsert) {
            const timer = setTimeout(() => {
                setHtmlContentToInsert("");
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [htmlContentToInsert]);

    const handleImageInsert = () => {
        if (imageUrl.trim()) {
            setShowImageModal(false);
            setTimeout(() => {
                setImageUrl("");
                setImageWidth("300");
                setImageHeight("200");
                setEditingImage(null);
            }, 100);
        }
    };

    const handleImageEdit = (imageNode) => {
        setEditingImage(imageNode);
        setImageUrl(imageNode.attrs.src);
        setImageWidth(imageNode.attrs.width || "300");
        setImageHeight(imageNode.attrs.height || "200");
        setShowImageModal(true);
    };

    const handleHtmlInsert = () => {
        if (htmlContent.trim()) {
            setHtmlContentToInsert(htmlContent);
            setShowHtmlModal(false);
            setHtmlContent("");
        }
    };

    // Preview modal states
    const [previewFile, setPreviewFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    // Track last applied template ID to prevent overwriting saved data on initial load
    const lastAppliedTemplateIdRef = useRef(followUp?.EmailTemplateId || followUp?.SmsTemplateId || emailTemplate?.id);


    // Sync showCc / showBcc when ccList / bccList are updated
    useEffect(() => {
        if (ccList && ccList.length > 0) setShowCc(true);
    }, [ccList]);

    useEffect(() => {
        if (bccList && bccList.length > 0) setShowBcc(true);
    }, [bccList]);

    // Sync emailTemplate with loaded templates to get full template details (body, subject, etc.)
    useEffect(() => {
        if (emailTemplate && emailTemplate.id && templates.length > 1) {
            const found = templates.find(t => String(t.id) === String(emailTemplate.id));
            if (found && found !== emailTemplate) {
                setEmailTemplate(found);
            }
        }
    }, [templates, emailTemplate, setEmailTemplate]);
    // Sync action mode based on followUp data when it loads
    useEffect(() => {
        if (followUp) {
            if (isEmail) setEmailAction(followUp?.EmailTemplateId ? "template" : "compose");
            if (isSMS) setSmsAction(followUp?.SmsTemplateId ? "template" : "compose");
        }
    }, [followUp, isEmail, isSMS]);

    // Clear selected template and action mode when activity type changes
    useEffect(() => {
        // Only reset if it's NOT an existing follow up being loaded
        if (!followUp) {
            setEmailTemplate({ id: 1, label: "-- Select Template --" });
            setEmailAction("compose");
            setSmsAction("compose");
            setTemplateVariables({});
            lastAppliedTemplateIdRef.current = 1;
        }
    }, [type, followUp]); // eslint-disable-line react-hooks/exhaustive-deps

    // Template selection and variable handling
    useEffect(() => {
        const action = isEmail ? emailAction : isSMS ? smsAction : null;
        if (action === "template" && emailTemplate && emailTemplate.id !== 1) {
            if (emailTemplate.id !== lastAppliedTemplateIdRef.current && (emailTemplate.body !== undefined || emailTemplate.subject !== undefined)) {
                const body = emailTemplate.body || '';
                const subject = emailTemplate.subject || '';
                const variables = extractTemplateVariables(body);
                const variableDefaults = {};
                variables.forEach(variable => {
                    switch (variable.toLowerCase()) {
                        case 'name':
                        case 'leadname': variableDefaults[variable] = lead?.name || ''; break;
                        case 'email': variableDefaults[variable] = lead?.email || ''; break;
                        case 'mobile':
                        case 'phone': variableDefaults[variable] = lead?.phone || lead?.cell || ''; break;
                        default: variableDefaults[variable] = '';
                    }
                });
                setTemplateVariables(variableDefaults);
                if (isEmail) {
                    setEmailSubject(replaceTemplateVariables(subject, variableDefaults));
                    setEmailBody(replaceTemplateVariables(body, variableDefaults));

                    if (emailTemplate.Attachments && Array.isArray(emailTemplate.Attachments)) {
                        const templateAttachments = emailTemplate.Attachments.map(att => ({
                            FileName: att.FileName,
                            FileSizeBytes: att.FileSizeBytes,
                            FilePath: att.FilePath,
                            AttachmentId: att.AttachmentId,
                            isTemplateAttachment: true
                        }));
                        setAttachments((prev) => {
                            const nonTemplateAtts = prev.filter(a => !a.isTemplateAttachment);
                            return [...nonTemplateAtts, ...templateAttachments];
                        });
                    }
                } else if (isSMS) {
                    setSmsBody(replaceTemplateVariables(body, variableDefaults));
                }
                lastAppliedTemplateIdRef.current = emailTemplate.id;
            }
        } else if (action === "compose") {
            if (emailTemplate?.id !== 1) {
                if (isEmail) {
                    setEmailSubject("");
                    setEmailBody("");
                    setAttachments((prev) => prev.filter(a => !a.isTemplateAttachment));
                } else if (isSMS) {
                    setSmsBody("");
                }
                setTemplateVariables({});
                setEmailTemplate({ id: 1, label: "-- No Template --" });
                lastAppliedTemplateIdRef.current = 1;
            }
        }
    }, [emailTemplate, emailAction, smsAction, lead, isEmail, isSMS]);

    const handleVariableChange = (variable, value) => {
        const updatedVars = { ...templateVariables, [variable]: value };
        setTemplateVariables(updatedVars);

        if (emailTemplate && emailTemplate.id !== 1) {
            if (isEmail) {
                setEmailBody(replaceTemplateVariables(emailTemplate.body || '', updatedVars));
                setEmailSubject(replaceTemplateVariables(emailTemplate.subject || '', updatedVars));
            } else if (isSMS) {
                setSmsBody(replaceTemplateVariables(emailTemplate.body || '', updatedVars));
            }
        }
    };

    // Tag helpers
    const addTag = (list, setList, setInput, value) => {
        const email = value.trim().replace(/,$/, "");
        if (email && !list.includes(email)) setList([...list, email]);
        setInput("");
    };
    const removeTag = (list, setList, idx) => setList(list.filter((_, i) => i !== idx));

    const handleTagKeyDown = (e, list, setList, input, setInput) => {
        if ((e.key === "Enter" || e.key === ",") && input.trim()) {
            e.preventDefault();
            addTag(list, setList, setInput, input);
        } else if (e.key === "Backspace" && !input && list.length) {
            setList(list.slice(0, -1));
        }
    };

    // Attachment helpers
    const addFiles = (files) => {
        const next = Array.from(files).filter(
            (f) => !attachments.find((a) => {
                const aName = a.name || a.FileName || a.fileName;
                const aSize = a.size !== undefined ? a.size : (a.FileSizeBytes !== undefined ? a.FileSizeBytes : a.fileSizeBytes);
                return aName === f.name && aSize === f.size;
            })
        );
        setAttachments((prev) => [...prev, ...next]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const formatBytes = (bytes) => {
        if (bytes === undefined || bytes === null || isNaN(bytes)) return "0 B";
        if (bytes < 1024) return bytes + " B";
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
        return (bytes / 1048576).toFixed(1) + " MB";
    };

    const isImageFile = (file) => {
        if (file.type) return file.type.startsWith('image/');
        const name = file.name || file.FileName || file.fileName || "";
        return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(name);
    };

    const isPdfFile = (file) => {
        if (file.type === 'application/pdf') return true;
        const name = file.name || file.FileName || file.fileName || "";
        return /\.pdf$/i.test(name);
    };

    const isDocFile = (file) => {
        const name = file.name || file.FileName || file.fileName || "";
        return /\.(doc|docx|csv|xls|xlsx|txt)$/i.test(name);
    };

    const isPreviewableFile = (file) => {
        return isImageFile(file) || isPdfFile(file) || isDocFile(file);
    };

    const openPreview = async (file) => {
        let url = null;
        if (file instanceof File) {
            url = URL.createObjectURL(file);
        } else if (file.url || file.FileUrl || file.Url || file.path || file.FilePath) {
            url = file.url || file.FileUrl || file.Url || file.path || file.FilePath;
        } else if (file.base64) {
            url = file.base64;
        }

        if (url && typeof url === 'string' && isStorageKey(url)) {
            const result = await getAttachmentPreviewUrl(url);
            if (result.success && result.url) {
                url = result.url;
            } else {
                toast.error(result.message || "Unable to preview attachment");
                return;
            }
        } else if (url && typeof url === 'string' && !url.startsWith('http') && !url.startsWith('blob:') && !url.startsWith('data:')) {
            const cleanUrl = url.replace(/^[/\\]+/, '');
            const baseUrl = typeof JWT_HOST_API === 'string' && JWT_HOST_API.endsWith('/') ? JWT_HOST_API : `${JWT_HOST_API || ''}/`;
            url = `${baseUrl}${cleanUrl.replace(/\\/g, '/')}`;
        }

        if (url) {
            setPreviewFile(file);
            setPreviewUrl(url);
        }
    };

    const closePreview = () => {
        if (previewFile instanceof File && previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewFile(null);
        setPreviewUrl(null);
    };

    return (
        <div className="space-y-6">
            {/* Mode Tabs */}
            <div className=" flex p-1 bg-gray-100 dark:bg-dark-800 rounded-xl w-max border border-gray-200 dark:border-dark-600 h-[45px]">
                <button
                    type="button"
                    onClick={() => isEmail ? setEmailAction("template") : setSmsAction("template")}
                    className={clsx(
                        "px-4 py-2 text-sm font-semibold rounded-lg transition-all",
                        (isEmail ? emailAction : smsAction) === "template"
                            ? "bg-white dark:bg-dark-700 shadow-sm text-gray-900 dark:text-white"
                            : "text-gray-500 hover:text-gray-700 dark:text-gray-400"
                    )}
                >
                    Use Template
                </button>
                <button
                    type="button"
                    onClick={() => isEmail ? setEmailAction("compose") : setSmsAction("compose")}
                    className={clsx(
                        "px-4 py-2 text-sm font-semibold rounded-lg transition-all",
                        (isEmail ? emailAction : smsAction) === "compose"
                            ? "bg-white dark:bg-dark-700 shadow-sm text-gray-900 dark:text-white"
                            : "text-gray-500 hover:text-gray-700 dark:text-gray-400"
                    )}
                >
                    Write Custom
                </button>
            </div>
            {/* Schedule Delivery (Shared) */}
            <div className="space-y-4">
                <EmailScheduleToggle
                    enabled={isScheduleDeliveryEnabled}
                    onToggle={() => {
                        const nextVal = !isScheduleDeliveryEnabled;
                        setIsScheduleDeliveryEnabled(nextVal);

                        if (nextVal) {
                            // Populate default date/time if empty when toggling ON
                            const now = new Date();
                            const currentHours = String(now.getHours()).padStart(2, '0');
                            const currentMinutes = String(now.getMinutes()).padStart(2, '0');
                            const currentTimeStr = `${currentHours}:${currentMinutes}`;

                            if (!preferredDeliveryDate) {
                                setPreferredDeliveryDate(now);
                            }
                            if (!preferredDeliveryTime) {
                                setPreferredDeliveryTime(currentTimeStr);
                            }
                        } else {
                            // Reset date & time when toggling OFF
                            setPreferredDeliveryDate(null);
                            setPreferredDeliveryTime("");
                            setErrors(prev => ({ ...prev, preferredDeliveryDate: null, preferredDeliveryTime: null }));
                        }
                    }}
                    scheduledDate={preferredDeliveryDate}
                    onDateChange={(val) => { setPreferredDeliveryDate(val); if (errors?.preferredDeliveryDate) setErrors(prev => ({ ...prev, preferredDeliveryDate: null })); }}
                    scheduledTime={preferredDeliveryTime}
                    onTimeChange={(val) => { setPreferredDeliveryTime(val); if (errors?.preferredDeliveryDate) setErrors(prev => ({ ...prev, preferredDeliveryDate: null })); }}
                    label={isEmail ? "Schedule Email Delivery" : "Schedule SMS Delivery"}
                    variant="full"
                    disabled={false}
                    isEditMode={!!followUp}
                />
                {errors?.preferredDeliveryDate && (
                    <div className="text-xs text-red-500 font-medium px-1 mt-1">
                        {errors.preferredDeliveryDate}
                    </div>
                )}
            </div>

            {/* Template Selector & Variables */}
            {(isEmail ? emailAction : smsAction) === "template" && (
                <div className="space-y-4 animate-in fade-in duration-300">
                    <StyledListbox
                        label={isEmail ? "Select Email Template" : "Select SMS Template"}
                        data={templates}
                        displayField="label"
                        placeholder={templatesLoading ? "Loading templates..." : "Choose a template"}
                        value={emailTemplate}
                        onChange={setEmailTemplate}
                    />
                </div>
            )}

            {/* Email Specific Fields */}
            {isEmail && (
                <div className="space-y-4 animate-in fade-in duration-300">
                    <div>
                        <Input
                            label="Subject"
                            required
                            maxLength={200}
                            error={errors?.emailSubject}
                            value={emailSubject}
                            onChange={(e) => {
                                setEmailSubject(e.target.value);
                                if (errors?.emailSubject) {
                                    setErrors(prev => ({ ...prev, emailSubject: null }));
                                }
                            }}
                            placeholder="Enter email subject"
                        />
                        <div className="flex justify-between items-center mt-1 px-1">
                            <span className="text-[10px] text-gray-400">Max 200 characters</span>
                            <span className="text-[10px] text-gray-400 font-semibold">{emailSubject?.length || 0}/200</span>
                        </div>
                    </div>

                    {/* CC / BCC Toggles */}
                    <div className="flex gap-2">
                        {!showCc && (
                            <button type="button" onClick={() => setShowCc(true)} className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-primary-600 transition-colors">
                                <PlusIcon className="size-3.5" /> Add CC
                            </button>
                        )}
                        {!showBcc && (
                            <button type="button" onClick={() => setShowBcc(true)} className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-primary-600 transition-colors">
                                <PlusIcon className="size-3.5" /> Add BCC
                            </button>
                        )}
                    </div>

                    {showCc && (
                        <div className="space-y-1.5 animate-in fade-in slide-in-from-top-2 duration-200">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">CC </label>
                                <button type="button" onClick={() => { setShowCc(false); setCcList([]); setCcInput(""); if (errors?.ccList) { setErrors(prev => ({ ...prev, ccList: null })); } }} className="text-xs text-red-500 font-semibold hover:text-red-400 transition-colors flex items-center gap-1">
                                    <XMarkIcon className="size-3.5" /> Remove
                                </button>
                            </div>
                            <div className={`flex flex-wrap gap-1.5 items-center min-h-[40px] px-3 py-1.5 rounded-xl border bg-white dark:bg-dark-750 focus-within:border-primary-500 transition-colors ${errors?.ccList ? "border-red-500 focus-within:border-red-500 focus-within:ring-red-500" : "border-gray-200 dark:border-dark-600"
                                }`}>
                                {ccList.map((email, i) => (
                                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 text-xs font-medium">
                                        {email}
                                        <button type="button" onClick={() => {
                                            removeTag(ccList, setCcList, i);
                                            if (errors?.ccList) {
                                                setErrors(prev => ({ ...prev, ccList: null }));
                                            }
                                        }} className="hover:text-red-500"><XMarkIcon className="size-3" /></button>
                                    </span>
                                ))}
                                <input
                                    value={ccInput}
                                    onChange={(e) => {
                                        setCcInput(e.target.value);
                                        if (errors?.ccList) {
                                            setErrors(prev => ({ ...prev, ccList: null }));
                                        }
                                    }}
                                    onKeyDown={(e) => handleTagKeyDown(e, ccList, setCcList, ccInput, setCcInput)}
                                    onBlur={() => ccInput.trim() && addTag(ccList, setCcList, setCcInput, ccInput)}
                                    placeholder={ccList.length ? "" : "Add CC recipients (press Enter)"}
                                    className="flex-1 min-w-[200px] text-sm bg-transparent outline-none text-gray-900 dark:text-white placeholder:text-gray-400 py-1"
                                />
                            </div>
                            <div className="flex justify-between items-center px-1">
                                {errors?.ccList ? (
                                    <span className="text-xs text-red-500 font-medium">{errors.ccList}</span>
                                ) : (
                                    <span className="text-[10px] text-gray-400">Max 500 characters when joined</span>
                                )}
                                <span className={`text-[10px] font-semibold ${ccList.join(',').length > 500 ? "text-red-500" : "text-gray-400"}`}>
                                    {ccList.join(',').length}/500
                                </span>
                            </div>
                        </div>
                    )}

                    {showBcc && (
                        <div className="space-y-1.5 animate-in fade-in slide-in-from-top-2 duration-200">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">BCC </label>
                                <button type="button" onClick={() => { setShowBcc(false); setBccList([]); setBccInput(""); if (errors?.bccList) { setErrors(prev => ({ ...prev, bccList: null })); } }} className="text-xs text-red-500 font-semibold hover:text-red-400 transition-colors flex items-center gap-1">
                                    <XMarkIcon className="size-3.5" /> Remove
                                </button>
                            </div>
                            <div className={`flex flex-wrap gap-1.5 items-center min-h-[40px] px-3 py-1.5 rounded-xl border bg-white dark:bg-dark-750 focus-within:border-primary-500 transition-colors ${errors?.bccList ? "border-red-500 focus-within:border-red-500 focus-within:ring-red-500" : "border-gray-200 dark:border-dark-600"
                                }`}>
                                {bccList.map((email, i) => (
                                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-xs font-medium">
                                        {email}
                                        <button type="button" onClick={() => {
                                            removeTag(bccList, setBccList, i);
                                            if (errors?.bccList) {
                                                setErrors(prev => ({ ...prev, bccList: null }));
                                            }
                                        }} className="hover:text-red-500"><XMarkIcon className="size-3" /></button>
                                    </span>
                                ))}
                                <input
                                    value={bccInput}
                                    onChange={(e) => {
                                        setBccInput(e.target.value);
                                        if (errors?.bccList) {
                                            setErrors(prev => ({ ...prev, bccList: null }));
                                        }
                                    }}
                                    onKeyDown={(e) => handleTagKeyDown(e, bccList, setBccList, bccInput, setBccInput)}
                                    onBlur={() => bccInput.trim() && addTag(bccList, setBccList, setBccInput, bccInput)}
                                    placeholder={bccList.length ? "" : "Add BCC recipients (press Enter)"}
                                    className="flex-1 min-w-[200px] text-sm bg-transparent outline-none text-gray-900 dark:text-white placeholder:text-gray-400 py-1"
                                />
                            </div>
                            <div className="flex justify-between items-center px-1">
                                {errors?.bccList ? (
                                    <span className="text-xs text-red-500 font-medium">{errors.bccList}</span>
                                ) : (
                                    <span className="text-[10px] text-gray-400">Max 500 characters when joined</span>
                                )}
                                <span className={`text-[10px] font-semibold ${bccList.join(',').length > 500 ? "text-red-500" : "text-gray-400"}`}>
                                    {bccList.join(',').length}/500
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Attachments</label>
                        <div
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                            className={clsx(
                                "flex flex-col sm:flex-row items-center gap-4 px-4 py-3 rounded-xl border-2 border-dashed transition-colors text-sm",
                                isDragging ? "border-primary-500 bg-primary-50 dark:bg-primary-900/10" : "border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-750"
                            )}
                        >
                            <label className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 dark:border-dark-600 font-semibold text-gray-700 dark:text-gray-200 hover:border-primary-500 hover:text-primary-600 cursor-pointer transition-colors shrink-0 bg-white dark:bg-dark-700 shadow-sm">
                                <PaperClipIcon className="size-4" />
                                Choose Files
                                <input type="file" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
                            </label>
                            <span className="text-gray-400 text-xs">
                                {isDragging ? "Drop files here..." : "or drag and drop files here"}
                            </span>
                        </div>
                        {attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-2">
                                {attachments.map((file, i) => {
                                    const name = file.name || file.FileName || file.fileName || "Attachment";
                                    const size = file.size !== undefined ? file.size : (file.FileSizeBytes !== undefined ? file.FileSizeBytes : file.fileSizeBytes);
                                    return (
                                        <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-dark-600 text-xs font-medium text-gray-700 dark:text-gray-300">
                                            <PaperClipIcon className="size-3.5 text-gray-400" />
                                            <span className="max-w-[160px] truncate">{name}</span>
                                            <span className="text-gray-400">({formatBytes(size)})</span>
                                            {isPreviewableFile(file) && (
                                                <button type="button" onClick={() => openPreview(file)} className="ml-2 text-primary-500 hover:text-primary-700 transition-colors" title="Preview">
                                                    <EyeIcon className="size-4" />
                                                </button>
                                            )}
                                            <button type="button" onClick={() => setAttachments(attachments.filter((_, j) => j !== i))} className="ml-1 text-gray-400 hover:text-red-500 transition-colors" title="Remove Attachment">
                                                <XMarkIcon className="size-4" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="space-y-1.5 pt-2">
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-1">
                                Message Body <span className="text-error">*</span>
                            </label>
                            <Button
                                type="button"
                                size="sm"
                                variant="outlined"
                                onClick={() => {
                                    setActiveEditor('email');
                                    setShowVariablesModal(true);
                                }}
                                className="text-xs py-1 px-2"
                            >
                                Manage Variables
                            </Button>
                        </div>
                        <div className="overflow-hidden">
                            <RichTextEditor
                                value={emailBody}
                                required
                                error={errors?.emailBody}
                                onChange={(val) => {
                                    setEmailBody(val);
                                    const len = getPlainTextLength(val);
                                    if (len > 2000) {
                                        setErrors(prev => ({ ...prev, emailBody: `Email body cannot exceed 2000 characters (${len}/2000)` }));
                                    } else if (errors?.emailBody) {
                                        setErrors(prev => ({ ...prev, emailBody: null }));
                                    }
                                }}
                                onPasteLimitExceeded={(msg) => setErrors(prev => ({ ...prev, emailBody: msg }))}
                                placeholder="Type your message here..."
                                height="300px"
                                onImageInsert={() => setShowImageModal(true)}
                                imageUrlToInsert={imageUrl}
                                imageWidth={imageWidth}
                                imageHeight={imageHeight}
                                onImageEdit={handleImageEdit}
                                editingImage={editingImage}
                                onHtmlInsert={() => {
                                    setHtmlContent("");
                                    setShowHtmlModal(true);
                                }}
                                htmlContentToInsert={htmlContentToInsert}
                                maxLength={2000}
                                limitType="html"
                            />
                        </div>
                        <div className="flex justify-between items-center px-1 mt-1">
                            <span className="text-[10px] text-gray-400">Max 2000 characters</span>
                            <span className={clsx(
                                "text-[10px] font-semibold",
                                getPlainTextLength(emailBody) > 1800
                                    ? "text-red-500 font-bold"
                                    : getPlainTextLength(emailBody) > 1500
                                        ? "text-amber-600"
                                        : "text-gray-400"
                            )}>
                                {getPlainTextLength(emailBody)}/2000
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* SMS Specific Fields */}
            {isSMS && (
                <div className="space-y-4 animate-in fade-in duration-300">
                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-1">
                                SMS Message <span className="text-error">*</span>
                            </label>
                            <Button
                                type="button"
                                size="sm"
                                variant="outlined"
                                onClick={() => {
                                    setActiveEditor('sms');
                                    setShowVariablesModal(true);
                                }}
                                className="text-xs py-1 px-2"
                            >
                                Manage Variables
                            </Button>
                        </div>
                        <div className="overflow-hidden">
                            <RichTextEditor
                                value={smsBody}
                                required
                                error={errors?.smsBody}
                                htmlContentToInsert={smsHtmlContentToInsert}
                                onChange={(val) => {
                                    setSmsBody(val);
                                    const textLen = stripHtml(val).length;
                                    if (textLen > 500) {
                                        setErrors(prev => ({ ...prev, smsBody: `SMS body cannot exceed 500 characters (${textLen}/500)` }));
                                    } else if (errors?.smsBody) {
                                        setErrors(prev => ({ ...prev, smsBody: null }));
                                    }
                                }}
                                onPasteLimitExceeded={(msg) => setErrors(prev => ({ ...prev, smsBody: msg }))}
                                placeholder="Type your SMS message here..."
                                height="200px"
                                enableVariableAutocomplete={true}
                                templateVariables={templateVariables}
                                maxLength={500}
                                limitType="text"
                            />
                        </div>
                        <div className="flex justify-between items-center px-1">
                            <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Supports variables like {'{{name}}'}, {'{{email}}'}</span>
                            <span className={clsx("text-xs font-semibold", stripHtml(smsBody).length > 500 ? "text-red-500 font-bold" : (stripHtml(smsBody).length > 160 ? "text-amber-600" : "text-gray-500"))}>
                                {stripHtml(smsBody).length}/500 chars | {Math.ceil(stripHtml(smsBody).length / 160)} SMS
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Rich Text Editor Modals */}
            <Transition appear show={showImageModal} as={Fragment}>
                <Dialog as="div" className="fixed inset-0 z-[1001]" onClose={() => setShowImageModal(false)}>
                    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm dark:bg-black/60" />
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <DialogPanel className="w-full max-w-md rounded-2xl bg-white dark:bg-dark-800 p-6 shadow-xl relative">
                            <DialogTitle className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">Insert Image</DialogTitle>
                            <div className="space-y-4">
                                <Input label="Image URL" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://" />
                                <div className="grid grid-cols-2 gap-4">
                                    <Input label="Width (px)" value={imageWidth} onChange={(e) => setImageWidth(e.target.value)} />
                                    <Input label="Height (px)" value={imageHeight} onChange={(e) => setImageHeight(e.target.value)} />
                                </div>
                                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-dark-700">
                                    <button type="button" className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors" onClick={() => setShowImageModal(false)}>Cancel</button>
                                    <button type="button" className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors" onClick={handleImageInsert} disabled={!imageUrl.trim()}>Insert</button>
                                </div>
                            </div>
                        </DialogPanel>
                    </div>
                </Dialog>
            </Transition>

            <Transition appear show={showHtmlModal} as={Fragment}>
                <Dialog as="div" className="fixed inset-0 z-[1001]" onClose={() => setShowHtmlModal(false)}>
                    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm dark:bg-black/60" />
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <DialogPanel className="w-full max-w-2xl rounded-2xl bg-white dark:bg-dark-800 p-6 shadow-xl relative">
                            <DialogTitle className="text-lg font-semibold mb-4 text-slate-900 dark:text-white">Insert HTML Content</DialogTitle>
                            <div className="space-y-4">
                                <Input
                                    as="textarea"
                                    value={htmlContent}
                                    onChange={(e) => setHtmlContent(e.target.value)}
                                    placeholder="<p>HTML here...</p>"
                                    rows={10}
                                    className="font-mono text-sm"
                                />
                                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-dark-700">
                                    <button type="button" className="px-4 py-2 text-sm font-semibold rounded-lg bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors" onClick={() => setShowHtmlModal(false)}>Cancel</button>
                                    <button type="button" className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors" onClick={handleHtmlInsert} disabled={!htmlContent.trim()}>Insert</button>
                                </div>
                            </div>
                        </DialogPanel>
                    </div>
                </Dialog>
            </Transition>

            {/* Image Preview Modal */}
            <Transition appear show={!!previewFile} as={Fragment}>
                <Dialog as="div" className="relative z-[9999]" onClose={closePreview}>
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
                    </TransitionChild>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <TransitionChild
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <DialogPanel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white dark:bg-dark-800 p-6 text-left align-middle shadow-xl transition-all relative flex flex-col">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Document Preview</h3>
                                        <button
                                            type="button"
                                            onClick={closePreview}
                                            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors shrink-0"
                                        >
                                            <XMarkIcon className="size-6" />
                                        </button>
                                    </div>
                                    <div className="flex-1 flex justify-center items-center w-full min-h-[300px] max-h-[75vh]">
                                        {previewUrl && isImageFile(previewFile) && (
                                            <img
                                                src={previewUrl}
                                                alt="Preview"
                                                className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-md"
                                            />
                                        )}
                                        {previewUrl && isPdfFile(previewFile) && (
                                            <iframe
                                                src={previewUrl}
                                                title="PDF Preview"
                                                className="w-full min-h-[70vh] rounded-lg shadow-md"
                                            />
                                        )}
                                        {previewUrl && isDocFile(previewFile) && (
                                            <div className="flex flex-col items-center justify-center p-8 bg-gray-50 dark:bg-dark-700 rounded-lg w-full h-full min-h-[50vh]">
                                                <DocumentIcon className="size-16 text-gray-400 mb-4" />
                                                <p className="text-gray-700 dark:text-gray-300 mb-4 font-medium text-lg">Document preview is not available in the browser.</p>
                                                <a href={previewUrl} target="_blank" rel="noopener noreferrer" download className="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-semibold shadow-sm">
                                                    Download / Open File
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            <ManageVariablesModal
                isOpen={showVariablesModal}
                onClose={() => setShowVariablesModal(false)}
                onInsert={(variable) => {
                    if (activeEditor === 'email') {
                        setHtmlContentToInsert(`{{${variable}}}`);
                    } else if (activeEditor === 'sms') {
                        setSmsHtmlContentToInsert(`{{${variable}}}`);
                    }
                    setShowVariablesModal(false);
                }}
            />
        </div>
    );
};
